import React, { useCallback, useMemo } from 'react';
import { Typography, Icon, Grid, Button, Box } from '@popmenu/common-ui';
import { Alert, AlertTitle } from '@popmenu/admin-ui';
import { Check, Error } from '@popmenu/web-icons';
import Loading from '~/shared/Loading';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import SelfSignUpStepContainer from '../../SelfSignUpStepContainer';
import useStyles from './styles';
import { useProgressStepManager } from './hooks/useProgressStepManager';
import { progressStepManagerOptions, type SelfSignUpFeedbackEvent } from './hooks/options';

const useMessages = (): Record<SelfSignUpFeedbackEvent, { description: string; title: string }> => {
  const intl = useIntl();

  return {
    firing_up_your_order: {
      description: intl.formatMessage({ defaultMessage: 'Configuring your ordering system and menu' }),
      title: intl.formatMessage({ defaultMessage: 'Firing Up Your Order' }),
    },
    prepping_your_profile: {
      description: intl.formatMessage({ defaultMessage: 'Setting up your account information and preferences' }),
      title: intl.formatMessage({ defaultMessage: 'Prepping Your Profile' }),
    },
    seasoning_your_design: {
      description: intl.formatMessage({ defaultMessage: 'Customizing your website design and branding' }),
      title: intl.formatMessage({ defaultMessage: 'Seasoning Your Design' }),
    },
    serving_up_your_my_pop_dashboard: {
      description: intl.formatMessage({ defaultMessage: 'Finalizing your dashboard and getting ready to serve' }),
      title: intl.formatMessage({ defaultMessage: 'Serving Up Your MyPop Dashboard' }),
    },
  };
};

type ProgressStepContentProps = {
  onRetry: () => void;
  onComplete: () => void;
  hasError: boolean;
  isRetrying: boolean;
};

export const ProgressStep = ({ onRetry, onComplete, hasError, isRetrying }: ProgressStepContentProps) => {
  const { errorMessage, stepManager, resetStepManager } = useProgressStepManager(onComplete, hasError);

  const classes = useStyles();

  const messages = useMessages();

  const hasAnyError = useMemo(() => hasError || Object.values(stepManager).some(status => status === 'error'), [stepManager, hasError]);

  const getStepStatus = useCallback((step: typeof progressStepManagerOptions.steps[number]) => {
    const status = stepManager[step.event];
    const isCompleted = status === 'completed';
    const isActive = status === 'pending' && !hasAnyError;
    const isError = status === 'error';
    return { isActive, isCompleted, isError, status };
  }, [hasAnyError, stepManager]);

  return (
    <SelfSignUpStepContainer data-testid="progress-step-container">
      <Grid container direction="column" justifyContent="flex-start" spacing={1} className={classes.progressHeader}>
        <Grid item>
          <Typography variant="h5" align="left">
            <FormattedMessage defaultMessage="Setting Up Your Account" />
          </Typography>
        </Grid>
        <Grid item>
          {hasAnyError ? (
            <Alert severity="error" className={classes.errorBanner}>
              <AlertTitle>
                <FormattedMessage defaultMessage="Setup Paused" />
              </AlertTitle>
              <Typography variant="body1" align="left">
                {errorMessage || <FormattedMessage defaultMessage="An error occurred while setting up your account. Please try again." />}
              </Typography>
              <Box mt={2}>
                <Button
                  data-tour-id="self-sign-up-progress-retry"
                  onClick={() => {
                    resetStepManager();
                    onRetry();
                  }}
                  variant="contained"
                  color="primary"
                  loading={isRetrying}
                >
                  <FormattedMessage defaultMessage="Retry" />
                </Button>
              </Box>
            </Alert>
          ) : (
            <Typography variant="body1" align="left">
              <FormattedMessage defaultMessage="Cooking up your Popmenu experience, we're almost finished setting up your account." />
            </Typography>
          )}
        </Grid>
      </Grid>
      {progressStepManagerOptions.steps.map((step) => {
        const { isCompleted, isActive, isError, status } = getStepStatus(step);
        const isPaused = hasAnyError && status === 'pending';

        let stepIcon = step.icon;
        if (isCompleted) {
          stepIcon = Check;
        } else if (isError) {
          stepIcon = Error;
        }

        return (
          <div
            key={step.event}
            className={classNames(classes.stepContainer, {
              [classes.stepContainerError]: isError,
              [classes.stepContainerPaused]: isPaused,
            })}
            data-testid={`progress-step-${step.event}`}
          >
            <div className={classes.iconContainer}>
              <div
                className={classNames(classes.circle, {
                  [classes.circleCompleted]: isCompleted,
                  [classes.circleError]: isError,
                  [classes.circlePaused]: isPaused,
                  [classes.circlePending]: !isCompleted && !isError && !isPaused,
                })}
                data-testid={`progress-step-circle-${status}`}
              >
                <Icon
                  icon={stepIcon}
                  className={classNames(classes.icon, {
                    [classes.checkIcon]: isCompleted,
                  })}
                />
              </div>
              <div className={classes.loadingWrapper}>
                {isActive ? <Loading size="md" /> : null}
              </div>
            </div>
            <div className={classes.stepContent}>
              <Typography
                variant="body1"
                className={classes.stepTitle}
                style={{ fontWeight: isActive || isCompleted ? 600 : 400 }}
              >
                {messages[step.event].title}
              </Typography>
              <Typography variant="body2" className={classes.stepDescription}>
                {messages[step.event].description}
              </Typography>
            </div>
          </div>
        );
      })}
      <Typography variant="body2" align="left">
        <FormattedMessage defaultMessage="Please, keep this tab open until the process is complete." />
      </Typography>
    </SelfSignUpStepContainer>
  );
};
